import React from 'react';
import { graphql, useFragment } from 'react-relay';
import Skeleton from '@atlaskit/skeleton';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import {
	PACKAGE_NAME,
	TEAM_NAME,
} from '@atlassian/jira-atlassian-navigation-nav4-common/src/common/constants.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type {
	ProductHomeRelay$key,
	JiraProjectType,
} from './__generated__/ProductHomeRelay.graphql';

type ProductHomeRelayProps = {
	queryReference: ProductHomeRelay$key;
	renderProductHome: React.ComponentType<{ projectType?: JiraProjectType | null | undefined }>;
};

export const ProductHomeRelayWithErrorBoundary = (
	props: ProductHomeRelayProps,
): React.JSX.Element => (
	<JSErrorBoundary
		id="Nav4TopNavProductHomeRelay"
		packageName={PACKAGE_NAME}
		teamName={TEAM_NAME}
		fallback="unmount"
	>
		<Placeholder
			name="nav4-top-nav-product-home-relay"
			fallback={<Skeleton isShimmering width="100px" height="20px" />}
		>
			<ProductHomeRelay {...props} />
		</Placeholder>
	</JSErrorBoundary>
);

export function ProductHomeRelay({
	queryReference,
	renderProductHome: ProductHomeComponent,
}: ProductHomeRelayProps): React.JSX.Element {
	const data = useFragment(
		graphql`
			fragment ProductHomeRelay on Query {
				jira {
					issueByKey(key: $issueKey, cloudId: $cloudId)
						@include(if: $shouldCheckProjectTypeForIssue) {
						projectField {
							project {
								projectType
							}
						}
					}
					jiraProjectByKey(key: $projectKey, cloudId: $cloudId)
						@include(if: $shouldCheckProjectType) {
						projectType
					}
				}
			}
		`,
		queryReference,
	);

	const issueProjectType =
		data?.jira?.jiraProjectByKey?.projectType ??
		data?.jira?.issueByKey?.projectField?.project?.projectType;

	return <ProductHomeComponent projectType={issueProjectType} />;
}
